import { Injectable, NgZone } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class InactivityService {
  private readonly INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 hour
  private readonly WARNING_TIME = 5 * 60 * 1000; // 5 minutes

  private lastActivityTime = Date.now();
  private inactivityTimer: any = null;
  private warningTimer: any = null;
  private isWarningShown = false;
  private isTracking = false;

  private readonly activityEvents = [
    'mousedown',
    'mousemove',
    'keypress',
    'scroll',
    'touchstart',
    'click',
  ];

  constructor(private ngZone: NgZone) {}

  startTracking(): void {
    if (this.isTracking) return;

    this.isTracking = true;
    this.resetTimer();
    this.addActivityListeners();
  }

  private addActivityListeners(): void {
    this.activityEvents.forEach((event) => {
      document.addEventListener(event, this.onActivity.bind(this), {
        passive: true,
        capture: true,
      });
    });
  }

  private onActivity(): void {
    if (!this.isTracking) return;

    this.ngZone.run(() => {
      this.resetActivity();
    });
  }

  private resetActivity(): void {
    this.lastActivityTime = Date.now();
    this.isWarningShown = false;
    this.resetTimer();
  }

  private resetTimer(): void {
    this.clearTimers();

    // Warning before logout
    this.warningTimer = setTimeout(() => {
      this.showInactivityWarning();
    }, this.INACTIVITY_TIMEOUT - this.WARNING_TIME);

    // Auto-logout
    this.inactivityTimer = setTimeout(() => {
      this.performLogout();
    }, this.INACTIVITY_TIMEOUT);
  }

  private clearTimers(): void {
    if (this.inactivityTimer) clearTimeout(this.inactivityTimer);
    if (this.warningTimer) clearTimeout(this.warningTimer);
    this.inactivityTimer = this.warningTimer = null;
  }

  private showInactivityWarning(): void {
    if (this.isWarningShown || !this.isTracking) return;

    const existing = document.getElementById('inactivity-warning');
    if (existing) return;

    this.isWarningShown = true;

    const overlay = document.createElement('div');
    overlay.id = 'inactivity-warning';
    Object.assign(overlay.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      right: '0',
      bottom: '0',
      background: 'rgba(0,0,0,0.4)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: '9999',
    });

    const dialog = document.createElement('div');
    Object.assign(dialog.style, {
      background: '#fff',
      padding: '24px',
      borderRadius: '8px',
      textAlign: 'center',
      boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
      width: '300px',
    });

    const title = document.createElement('h3');
    title.textContent = 'Session Timeout Warning';

    const message = document.createElement('p');
    message.textContent = 'You will be logged out in 5 minutes due to inactivity.';

    const stayBtn = document.createElement('button');
    stayBtn.textContent = 'Stay Logged In';
    stayBtn.onclick = () => {
      this.dismissWarning();
      this.resetActivity();
    };

    const logoutBtn = document.createElement('button');
    logoutBtn.textContent = 'Logout Now';
    logoutBtn.onclick = () => {
      this.dismissWarning();
      this.performLogout();
    };

    dialog.append(title, message, stayBtn, logoutBtn);
    overlay.appendChild(dialog);
    document.body.appendChild(overlay);
  }

  private dismissWarning(): void {
    const warning = document.getElementById('inactivity-warning');
    if (warning) warning.remove();
    this.isWarningShown = false;
  }

  private performLogout(): void {
    if (!this.isTracking) return;

    this.stopTracking();
    this.dismissWarning();

    // Clear any cached data
    localStorage.clear();
    sessionStorage.clear();

    // Redirect to login or home page
    window.location.href = '/login';
  }

  stopTracking(): void {
    if (!this.isTracking) return;

    this.isTracking = false;
    this.clearTimers();

    this.activityEvents.forEach((event) => {
      document.removeEventListener(event, this.onActivity.bind(this));
    });
  }
}
